import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { Tooltip, makeStyles } from '@material-ui/core/';
import { useTranslation, Trans } from 'react-i18next';

import { localeDate } from 'Util/datetime';
import { Text, Flex, SvgIcon } from 'Components/Base';
import ButtonOutline from 'Components/Base/Button/ButtonOutlined';

const CURRENCIES = {
  THB: '฿',
};

const DISCOUNT_UNIT = {
  FIX: 'FIX',
  PERCENT: 'PERCENT',
};

const DiscountBox = styled((props) => (
  <Flex
    width={1}
    maxWidth={'372px'}
    justifyContent={'space-between'}
    alignItems={'center'}
    py={'16px'}
    px={'24px'}
    color={'info'}
    bg={'greyscale.grey5'}
    {...props}
  />
))`
  border-radius: 28px;
  gap: 8px;
  word-break: break-all;
`;

const DiscountButton = styled(ButtonOutline)`
  padding: 2px 10px;
  font-size: 14px;

  &:disabled {
    border-color: #d9d9d9;
    color: #a6a6a6;
  }
`;

const useToolTipStyles = makeStyles({
  tooltip: ({ colors }) => ({
    background: '#404040',
    color: colors.white,
    textAlign: 'center',
    borderRadius: '8px',
  }),
});

const getIsDiscountExpired = (expiredDate) => {
  const today = new Date();
  return today.getTime() >= expiredDate.getTime();
};

const PersonalizedDiscount = ({
  isTooltipOpen,
  isError,
  discountDescription,
  discountCode,
  discountExpiredAt,
  onCopyButtonClick,
}) => {
  const { t } = useTranslation('classroom');
  const { colors } = useTheme();
  const tooltipClasses = useToolTipStyles({ colors });
  const expiredDate = new Date(discountExpiredAt);
  const isDiscountExpired = useMemo(() => getIsDiscountExpired(expiredDate), []);

  const copiedComponent = (
    <Flex alignItems={'center'}>
      <SvgIcon icon={SvgIcon.ICON_LIST.CHECK} size={16} mr={1} />
      <Text textStyle="body2" color={'white'}>
        Copied
      </Text>
    </Flex>
  );
  return (
    <>
      <Flex mt={7} alignItems={'center'}>
        <SvgIcon mr={2} icon={SvgIcon.ICON_LIST.FILL_GIFT} color={'primary'} size={24} />
        <Text textStyle="heading5" textAlign={'center'}>
          {t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.TITLE')}
          {` `}
          {discountDescription?.discount}
          {discountDescription?.discountUnit === DISCOUNT_UNIT.PERCENT && '%'}
          {` `}
          {discountDescription?.discountUnit === DISCOUNT_UNIT.FIX &&
            CURRENCIES?.[discountDescription?.currency]}
        </Text>
      </Flex>
      <Text textStyle="body2" mt={2} textAlign={'center'}>
        {t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.DESCRIPTION')}
      </Text>
      {!isError && (
        <Text
          textStyle="caption"
          mt={2}
          color={isDiscountExpired ? 'danger' : 'greyscale.grey3'}
          textAlign={'center'}
        >
          {t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.EXPIRED_DATE')}{' '}
          {localeDate(expiredDate, 'th', 'DD/MM/YYYY')}
        </Text>
      )}
      <DiscountBox mt={6}>
        <Flex width={1} alignItems={'center'}>
          {isError ? (
            <Flex width={1} justifyContent={'center'} alignItems={'center'}>
              <SvgIcon mr={2} icon={SvgIcon.ICON_LIST.DANGER} size={24} color={'danger'} />
              <Text
                textStyle="body2"
                color={'danger'}
                fontWeight="medium"
                style={{ lineHeight: 'normal' }}
              >
                {t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.DISCOUNT_ERROR')}
              </Text>
            </Flex>
          ) : (
            <Text
              textStyle="body2"
              color={isDiscountExpired ? '#a6a6a6' : 'info'}
              fontWeight="medium"
              style={{ lineHeight: 'normal' }}
            >
              {discountCode}
            </Text>
          )}
        </Flex>
        {!isError && (
          <Tooltip
            title={copiedComponent}
            open={isTooltipOpen}
            classes={tooltipClasses}
            placement={'top'}
            arrow
          >
            <DiscountButton onClick={onCopyButtonClick} disabled={isDiscountExpired}>
              {t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.COPY_BUTTON')}
            </DiscountButton>
          </Tooltip>
        )}
      </DiscountBox>
      {isError ? (
        <Text textStyle="caption" mt={3} textAlign={'center'}>
          <Trans
            defaults={`${t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.DISCOUNT_ERROR_CONTACT')}`}
            components={[<br key="contact" />]}
          />
          {` `}
          <Text as="span" textStyle="caption" color={'info'}>
            support@skooldio.com
          </Text>
        </Text>
      ) : (
        <Text textStyle="caption" mt={3} color={'danger'} textAlign={'center'}>
          {t('GRADUATE_MODAL.PERSONALIZED_DISCOUNT.REMINDER')}
        </Text>
      )}
    </>
  );
};

PersonalizedDiscount.propTypes = {
  isTooltipOpen: PropTypes.bool,
  isError: PropTypes.bool,
  discountDescription: PropTypes.shape({
    discount: PropTypes.number,
    currency: PropTypes.string,
    discountUnit: PropTypes.string,
  }),
  discountCode: PropTypes.string,
  discountExpiredAt: PropTypes.string,
  onCopyButtonClick: PropTypes.func,
};

PersonalizedDiscount.defaultProps = {
  isTooltipOpen: false,
  isError: false,
  onCopyButtonClick: () => {},
};

export default PersonalizedDiscount;
